// src/components/GroupMembersModal.tsx

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonBadge,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { close, person, shieldCheckmark } from 'ionicons/icons';
import { getGroupDetails, GroupMember } from '../services/groupService';
import { supabase } from '../services/supabase';
import { AvatarPreview } from './avatar/AvatarPreview';

interface GroupMembersModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  groupId: string;
  groupName?: string;
}

export const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
  isOpen,
  onDismiss,
  groupId,
  groupName,
}) => {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  useEffect(() => {
    if (isOpen && groupId) {
      loadMembers();
    }
  }, [isOpen, groupId]);

  const loadMembers = async () => {
    setLoading(true);

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }

    // Get group details with members
    const result = await getGroupDetails(groupId);
    if (result.success && result.group?.members) {
      // Sort members: creator first, then admins, then regular members
      const sortedMembers = result.group.members.sort((a, b) => {
        const roleOrder = { creator: 0, admin: 1, member: 2 };
        return roleOrder[a.role] - roleOrder[b.role];
      });
      setMembers(sortedMembers);
    }

    setLoading(false);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'creator':
        return <IonBadge color="warning">Creator</IonBadge>;
      case 'admin':
        return <IonBadge color="primary">Admin</IonBadge>;
      default:
        return null;
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'creator':
        return shieldCheckmark;
      case 'admin':
        return shieldCheckmark;
      default:
        return person;
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>{groupName || 'Group'} Members</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner name="crescent" />
          </div>
        ) : members.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '20px', textAlign: 'center' }}>
            <IonText color="medium">
              <p>No members found</p>
            </IonText>
          </div>
        ) : (
          <IonList>
            <IonItem lines="none" style={{ padding: '16px', background: 'var(--ion-color-light)' }}>
              <IonLabel>
                <IonText color="medium">
                  <p style={{ margin: 0 }}>{members.length} {members.length === 1 ? 'member' : 'members'}</p>
                </IonText>
              </IonLabel>
            </IonItem>

            {members.map((member) => (
              <IonItem key={member.id}>
                <IonAvatar slot="start">
                  {member.profile?.avatar_url ? (
                    <img
                      src={member.profile.avatar_url}
                      alt={member.profile.display_name || member.profile.username}
                    />
                  ) : member.profile?.avatar ? (
                    <AvatarPreview
                      avatarData={member.profile.avatar}
                      size={40}
                      userId={member.user_id}
                      isAdmin={member.profile.is_admin || false}
                    />
                  ) : (
                    <AvatarPreview size={40} userId={member.user_id} />
                  )}
                </IonAvatar>

                <IonLabel>
                  <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {member.profile?.display_name || member.profile?.username || 'Unknown User'}
                    {member.user_id === currentUserId && (
                      <IonText color="medium" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        (You)
                      </IonText>
                    )}
                  </h2>
                  {member.profile?.display_name && (
                    <p>@{member.profile.username}</p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                    Joined {new Date(member.joined_at).toLocaleDateString()}
                  </p>
                </IonLabel>

                <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getRoleBadge(member.role)}
                </div>
              </IonItem>
            ))}
          </IonList>
        )}
      </IonContent>
    </IonModal>
  );
};